import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
    RemoteNavItem,
    RemoteSidebarMountApi,
    RemoteSidebarMountFn,
    RemoteSidebarMountOptions,
    RemoteUserMenuItem
} from '@platform-ui/remote-types';
import { env } from '@/config/env';
import { UserMenuItems } from './user-menu-items';
import { authService } from '@/auth';
import { MountStatus } from '@/domain/types';
import { capabilityBus } from '@/services/capability-bus';

import { Spinner } from '@aero/ui';

/** Selector for the user menu slot in the remote sidebar */
const USER_MENU_SLOT_SELECTOR = '[data-mf-container="user-menu-items-client"]';

function defer(fn: () => void): void {
    if (typeof queueMicrotask === 'function') {
        queueMicrotask(fn);
        return;
    }
    setTimeout(fn, 0);
}

function deferUnmount(root: Root): void {
    defer(() => {
        root.unmount();
    });
}

interface SidebarModule {
    getRemoteSidebarMount: RemoteSidebarMountFn;
}

/**
 * Props for the SidebarHost component.
 */
type SidebarHostProps = {
    navItems: RemoteNavItem[];
    homeNavItems: RemoteNavItem[];
    isHomeApp: boolean;
    rootNavLabel?: string;
    currentAppName?: string;
    isCollapsed: boolean;
    onToggle: () => void;
    userMenuItems?: RemoteUserMenuItem[];
    isNavLoading?: boolean;
    isMobile?: boolean;
    openMobile?: boolean;
    onOpenMobileChange?: (open: boolean) => void;
};

/**
 * Sidebar mount options with required fields for the host.
 * Extends the base options making certain fields required.
 */
type SidebarMountOptions = Required<
    Pick<RemoteSidebarMountOptions, 'navItems' | 'homeNavItems' | 'isHomeApp' | 'isCollapsed' | 'onToggle'>
> &
    Omit<RemoteSidebarMountOptions, 'navItems' | 'homeNavItems' | 'isHomeApp' | 'isCollapsed' | 'onToggle'>;

/**
 * Lightweight placeholder shown while the remote sidebar module is loading.
 * Matches the sidebar's width to prevent layout shift.
 */
function SidebarLoadingFallback({ isCollapsed, isMobile }: { isCollapsed: boolean; isMobile: boolean }) {
    if (isMobile) return null;
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                height: '100%',
                width: isCollapsed ? '3.5rem' : '15rem',
                backgroundColor: '#f8fafc'
            }}>
            <Spinner className="size-3" />
            {!isCollapsed && <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Loading sidebar...</p>}
        </div>
    );
}

/**
 * Host component for the remote sidebar module.
 *
 * Loads and mounts the federated sidebar from portal-core, handling:
 * - Async module loading with error handling
 * - Reactive prop updates via the update() API
 * - User menu portal injection via MutationObserver
 * - Loading state management
 *
 * The sidebar remote exposes a slot for user menu items which this host
 * populates by rendering React content into it when the slot becomes available.
 */
export function SidebarHost({
    navItems,
    homeNavItems,
    isHomeApp,
    rootNavLabel,
    currentAppName,
    isCollapsed,
    onToggle,
    userMenuItems = [],
    isNavLoading = false,
    isMobile = false,
    openMobile = false,
    onOpenMobileChange
}: SidebarHostProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const apiRef = useRef<RemoteSidebarMountApi | null>(null);
    const pendingUpdateRef = useRef<SidebarMountOptions | null>(null);
    const userMenuRootRef = useRef<Root | null>(null);
    const lastUserMenuContainerRef = useRef<HTMLElement | null>(null);

    const location = useLocation();
    const navigate = useNavigate();

    const [status, setStatus] = useState<MountStatus>('loading');

    // Build current props for mount/update
    const buildMountOptions = useCallback(
        (): SidebarMountOptions => ({
            navItems,
            homeNavItems,
            isHomeApp,
            rootNavLabel,
            currentAppName,
            currentPath: location.pathname,
            onItemClick: (item: RemoteNavItem) => {
                if (!item.path) return;
                if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
                    window.open(item.path, '_blank', 'noopener,noreferrer');
                } else {
                    navigate(item.path);
                }
            },
            isCollapsed,
            onToggle,
            federatedAuth: authService.createFederatedAuth(),
            isNavLoading,
            isMobile,
            openMobile,
            onOpenMobileChange,
            capabilityBus
        }),
        [
            navItems,
            homeNavItems,
            isHomeApp,
            rootNavLabel,
            currentAppName,
            location.pathname,
            isCollapsed,
            onToggle,
            navigate,
            isNavLoading,
            isMobile,
            openMobile,
            onOpenMobileChange
        ]
    );

    // Mount the sidebar (runs once)
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                if (!env.sidebar?.importRemoteSidebarMount) {
                    console.warn('[SidebarHost] No sidebar configured in env');
                    setStatus('error');
                    return;
                }

                const module = (await env.sidebar.importRemoteSidebarMount()) as SidebarModule;

                if (cancelled || !containerRef.current) return;

                apiRef.current = module.getRemoteSidebarMount(containerRef.current, buildMountOptions());
                setStatus('mounted');

                // Apply any props that changed while mounting
                if (pendingUpdateRef.current) {
                    apiRef.current.update(pendingUpdateRef.current);
                    pendingUpdateRef.current = null;
                }
            } catch (err) {
                console.error('[SidebarHost] Failed to mount:', err);
                if (!cancelled) setStatus('error');
            }
        })();

        return () => {
            cancelled = true;
        };
        // Only mount once - updates handled separately
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update sidebar when props change
    useEffect(() => {
        const options = buildMountOptions();

        if (apiRef.current) {
            apiRef.current.update(options);
        } else {
            // Mount not complete yet, queue the update
            pendingUpdateRef.current = options;
        }
    }, [buildMountOptions]);

    // Portal user menu items into the sidebar's slot
    useEffect(() => {
        const renderUserMenu = (container: HTMLElement) => {
            if (container !== lastUserMenuContainerRef.current) {
                // Cleanup old root
                if (userMenuRootRef.current) {
                    deferUnmount(userMenuRootRef.current);
                    userMenuRootRef.current = null;
                }

                // Create new root for new container
                userMenuRootRef.current = createRoot(container);
                lastUserMenuContainerRef.current = container;
            }

            userMenuRootRef.current?.render(
                <UserMenuItems
                    navigate={navigate}
                    items={userMenuItems}
                />
            );
        };

        const findAndRenderSlot = () => {
            const slot =
                (containerRef.current?.querySelector(USER_MENU_SLOT_SELECTOR) as HTMLElement | null) ||
                (document.body.querySelector(USER_MENU_SLOT_SELECTOR) as HTMLElement | null);
            if (slot) renderUserMenu(slot);
        };

        // Initial attempt
        findAndRenderSlot();

        // Watch for slot to appear (remote may render async and/or portaled to body)
        const observer = new MutationObserver(findAndRenderSlot);

        if (containerRef.current) {
            observer.observe(containerRef.current, { childList: true, subtree: true });
        }

        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [navigate, userMenuItems]);

    // Cleanup on unmount
    useLayoutEffect(() => {
        return () => {
            const api = apiRef.current;
            if (api) {
                defer(() => {
                    api.unmount();
                });
            }
            apiRef.current = null;

            if (userMenuRootRef.current) {
                deferUnmount(userMenuRootRef.current);
                userMenuRootRef.current = null;
            }
        };
    }, []);

    // Error state - could show fallback UI
    if (status === 'error') {
        return null;
    }

    return (
        <div
            ref={containerRef}
            data-mf-container-host="sidebar"
            style={{
                width: isMobile ? undefined : isCollapsed ? '3.5rem' : '15rem',
                flexShrink: 0,
                position: 'relative'
            }}>
            {status === 'loading' && (
                <SidebarLoadingFallback
                    isCollapsed={isCollapsed}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
}
